import { Card, Chip, Typography } from "@mui/material";
import CustomTypography from "../../../Components/Typography";
import Box from "@mui/material/Box";
import InputField from "../../../Components/InputField";
import { useRef, useState } from "react";
import muiStyles from "../styles/muiStyles";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useFormikContext } from "formik";
import AddressMapInput from "../../../Components/AddressMapInput";

const MakeCommunityShine = ({
  disableFields,
  tags,
  selectedItems,
  setSelectedItems,
}) => {
  const {
    handleChange,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    values,
    touched,
    errors,
  } = useFormikContext();
  const { t } = useTranslation("page-community-settings");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isDropdownSelection, setIsDropdownSelection] = useState(false);
  const dropdownRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const handleAddItem = (item) => {
    if (item && !selectedItems.includes(item)) {
      const newItems = [...selectedItems, item];
      setSelectedItems(newItems);
      setSearchQuery("");
      setFilteredItems([]);
    }
  };

  const handleDeleteChip = (indexToDelete) => {
    const newItems = selectedItems.filter(
      (_, index) => index !== indexToDelete
    );
    setSelectedItems(newItems);
  };

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query) {
      const filtered = tags.filter(
        (tag) =>
          tag.toLowerCase().includes(query.toLowerCase()) &&
          !selectedItems.includes(tag)
      );
      setFilteredItems(filtered);
      setHighlightedIndex(-1);
    } else {
      setFilteredItems([]);
      setHighlightedIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      e.preventDefault();
      const itemToAdd =
        highlightedIndex >= 0 ? filteredItems[highlightedIndex] : searchQuery;
      handleAddItem(itemToAdd);
      setHighlightedIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : prev
      );
      if (dropdownRef.current && highlightedIndex < filteredItems.length - 1) {
        const nextItem = dropdownRef.current.children[highlightedIndex + 1];
        if (nextItem) nextItem.scrollIntoView({ block: "nearest" });
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      if (dropdownRef.current && highlightedIndex > 0) {
        const prevItem = dropdownRef.current.children[highlightedIndex - 1];
        if (prevItem) prevItem.scrollIntoView({ block: "nearest" });
      }
    }
  };

  const handleInputBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      if (searchQuery.trim() && !isDropdownSelection) {
        handleAddItem(searchQuery);
      }
      setSearchQuery("");
      setFilteredItems([]);
      setHighlightedIndex(-1);
      setIsDropdownSelection(false);
    }, 100);
  };

  const handleDropdownClick = (item) => {
    setIsDropdownSelection(true);
    handleAddItem(item);
    setSearchQuery("");
    setFilteredItems([]);
    setHighlightedIndex(-1);
    setIsDropdownSelection(false);
  };

  const {
    cardStyles,
    inputGridStyles,
    tagsContainerStyles,
    tagsInputBoxStyles,
    chipStyles,
    tagsDropdownContainerStyles,
    tagsDropdownListStyles,
    tagsDropdownItemStyles,
  } = muiStyles;

  return (
    <Card sx={cardStyles}>
      <CustomTypography type="subheading">
        {t("make_community_shine.title")}
      </CustomTypography>

      <Box sx={{ ...inputGridStyles, pt: "32px", pb: "32px" }}>
        <InputField
          name="community_name"
          label={t("make_community_shine.community_name")}
          value={values.community_name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("make_community_shine.community_name")}
          disabled={disableFields}
          error={touched.community_name && Boolean(errors.community_name)}
          helperText={touched.community_name && errors.community_name}
        />

        <AddressMapInput
          label={t("make_community_shine.location")}
          placeholder={t("make_community_shine.location")}
          disabled={disableFields}
          value={
            typeof values.community_location === "object"
              ? values.community_location?.address
              : values.community_location
          }
          onPlaceSelected={(placeDetails) => {
            setFieldValue("community_location", placeDetails);
            setFieldTouched("community_location", false);
          }}
          onBlur={() => setFieldTouched("community_location", true)}
          error={
            touched.community_location && Boolean(errors.community_location)
          }
          helperText={touched.community_location && errors.community_location}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          pb: "32px",
          opacity: disableFields ? 0.5 : 1,
          pointerEvents: disableFields ? "none" : "auto",
        }}
      >
        <CustomTypography type="label">
          {t("make_community_shine.tags_label")}
        </CustomTypography>

        <Box sx={tagsContainerStyles}>
          <Box
            sx={{
              ...tagsInputBoxStyles,
              backgroundColor: disableFields
                ? "rgba(248, 247, 250, 1)"
                : "transparent",
            }}
          >
            {selectedItems.map((item, index) => (
              <Chip
                key={`${item}-${index}`}
                label={item}
                onDelete={() => handleDeleteChip(index)}
                sx={chipStyles}
                deleteIcon={<CloseIcon />}
              />
            ))}
            <input
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleInputBlur}
              placeholder={
                selectedItems.length === 0
                  ? t("make_community_shine.tags_placeholder")
                  : ""
              }
              style={{
                flex: 1,
                minWidth: "auto",
                border: "none",
                outline: "none",
                backgroundColor: "transparent",
              }}
            />
          </Box>

          {searchQuery && filteredItems.length > 0 && (
            <Box sx={tagsDropdownContainerStyles}>
              <Box sx={tagsDropdownListStyles} ref={dropdownRef}>
                {filteredItems.map((item, index) => (
                  <Box
                    key={item}
                    onMouseDown={() => handleDropdownClick(item)}
                    sx={{
                      ...tagsDropdownItemStyles,
                      backgroundColor:
                        highlightedIndex === index ? "#e0e0e0" : "transparent",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="textSecondary">
            {t("make_community_shine.tags_hint")}
          </Typography>
        </Box>
      </Box>

      <InputField
        name="description"
        label={t("make_community_shine.description")}
        value={values.description}
        onChange={handleChange}
        placeholder={t("make_community_shine.description_placeholder")}
        disabled={disableFields}
        multiline
        minRows={4}
        maxRows={6}
        maxLength={1024}
      />
    </Card>
  );
};

export default MakeCommunityShine;
